import { useEffect, useState } from 'react';

// The bottom dock (search row and tab bar) marks its root with this attribute.
// Its own search field is the one editable element that must not hide it.
export const DOCK_ATTRIBUTE = 'data-mobile-dock';

// Inputs that do not raise a keyboard.
const NO_KEYBOARD = new Set([
    'button',
    'checkbox',
    'color',
    'file',
    'image',
    'radio',
    'range',
    'reset',
    'submit',
]);

/** True for an element the user types into (or picks a value in). */
export function isEditable(target: EventTarget | null): target is HTMLElement {
    if (!(target instanceof HTMLElement)) return false;
    if (target instanceof HTMLInputElement)
        return !NO_KEYBOARD.has(target.type) && !target.readOnly;
    if (target instanceof HTMLTextAreaElement) return !target.readOnly;
    if (target instanceof HTMLSelectElement) return true;
    const editable = target.getAttribute('contenteditable');
    return editable !== null && editable !== 'false';
}

const outsideDock = (target: EventTarget | null) =>
    isEditable(target) && target.closest(`[${DOCK_ATTRIBUTE}]`) === null;

/**
 * True while an editable element outside the dock has focus.
 *
 * On Android, `interactive-widget=resizes-content` shrinks the layout viewport
 * to what the keyboard leaves free, so a fixed bottom dock rides on top of the
 * keyboard for every field on the page and eats the room the field needs (the
 * chat's reply box is left with a third of the screen). While this is true the
 * dock steps aside. Typing in the dock's own search field is not that: that
 * field is meant to ride on the keyboard.
 *
 * `enabled` is false where there is no dock (wide screens): always false.
 */
export function useEditingOutsideDock(enabled: boolean): boolean {
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!enabled) {
            setEditing(false);
            return;
        }
        // Focus that was already in a field when the dock appeared.
        setEditing(outsideDock(document.activeElement));
        const onFocusIn = (e: FocusEvent) => setEditing(outsideDock(e.target));
        // Focus moving to another field is a focusout followed by a focusin:
        // look at where it goes, so the dock does not flash back in between.
        const onFocusOut = (e: FocusEvent) =>
            setEditing(outsideDock(e.relatedTarget));
        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);
        return () => {
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
        };
    }, [enabled]);

    return enabled && editing;
}
